import type { AgentAuditEntry } from '@inbound/contract';

import { useGetAgentAuditQuery } from '../../store/agents-api.js';
import { when } from '../../ui/when.js';
import styles from './AgentEditor.module.css';

/** Long values (instructions) are cut: the row says what changed, not the whole text. */
const CUT = 120;

function short(value: string): string {
  const flat = value.replace(/\s+/g, ' ').trim();
  return flat.length > CUT ? `${flat.slice(0, CUT)}…` : flat;
}

function describe(entry: AgentAuditEntry): string {
  if (entry.fieldDiff === null) return entry.action.toLowerCase();
  return Object.keys(entry.fieldDiff).join(', ');
}

/**
 * Who changed what, and when (S10).
 *
 * Changes made from this console, with the person who made them. Greeting
 * changes are recorded here too, on the agent that speaks them.
 */
export function AgentHistory({ agentId }: { agentId: string }): JSX.Element {
  const { data } = useGetAgentAuditQuery(agentId);
  const items = data?.items ?? [];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <h3>History</h3>
        <span className={styles.sub}>Changes made from this console</span>
      </div>
      <div className={styles.body}>
        {items.length === 0 ? (
          <span className={styles.toggleHint}>No changes yet.</span>
        ) : (
          <ul className={styles.history}>
            {items.map((entry) => (
              <li key={entry.id}>
                <span className={styles.historyHead}>
                  <strong>{describe(entry)}</strong>
                  <span>{entry.userEmail ?? 'Someone outside this console'}</span>
                  <span>· {when(entry.createdAt)}</span>
                </span>
                {entry.fieldDiff !== null &&
                  Object.entries(entry.fieldDiff).map(([field, { from, to }]) => (
                    <span key={field} className={styles.diff}>
                      {short(from) || '(empty)'} → {short(to) || '(empty)'}
                    </span>
                  ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
